import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Crown, Heart, Target, Users } from 'lucide-react'
import ScrollArrow from './ScrollArrow'
import { mockStatsData } from '../data/mockData'
import { API_CONFIG, getApiHeaders } from '../config/api'

interface AboutProps {
  onApplyClick: () => void
  onViewFullHistory: () => void
}

const About: React.FC<AboutProps> = ({ onApplyClick, onViewFullHistory }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [apiData, setApiData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)
  const [isStoryOpen, setIsStoryOpen] = useState(false)

  // Close modal on Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsStoryOpen(false)
      }
    }
    if (isStoryOpen) {
      window.addEventListener('keydown', onKeyDown)
    }
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isStoryOpen])

  // Lock body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    if (isStoryOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = originalOverflow
    }
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isStoryOpen])

  // Flying icons configuration - равномерное распределение как в Team
  const flyingIcons = useMemo(() => {
    const icons = [
      '👨‍👩‍👧‍👦', // семья
      '📜', // документ/история
      '⏳', // время
      '🔍', // поиск/исследование
      '📅', // календарь
      '📈', // рост/статистика
      '🌀', // вихрь/развитие
      '🧩', // пазл/единство
      '🏆', // трофей/достижения
    ];
    
    return Array.from({ length: window.innerWidth <= 768 ? 20 : 30 }, (_, index) => ({
      id: index,
      left: (index % 6) * 16.67 + Math.random() * 10, // 6 колонок с небольшим разбросом
      top: Math.floor(index / 6) * 20 + Math.random() * 15, // 5 рядов с небольшим разбросом
      fontSize: Math.random() * 2 + 1.5,
      icon: icons[index % icons.length],
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5,
      xOffset: Math.random() * 50 - 25
    }));
  }, []);

  const values = [
    {
      icon: Crown,
      title: 'Превосходство',
      shortDescription: 'Стремимся к совершенству во всем',
      fullDescription: 'Мы стремимся к совершенству во всем, что делаем, - от внутриигровых бенефитов до лучших OOC условий'
    },
    {
      icon: Heart,
      title: 'Лояльность',
      shortDescription: 'Верность семье и общим ценностям',
      fullDescription: 'Верность членам нашей семьи и приверженность нашим общим ценностям и целям.'
    },
    {
      icon: Target,
      title: 'Цели',
      shortDescription: 'Помогаем раскрыть потенциал',
      fullDescription: 'Мы преследуем амбициозные цели и помогаем нашим софамам полностью раскрыть свой потенциал.'
    },
    {
      icon: Users,
      title: 'Единство',
      shortDescription: 'Прочные узы для успеха',
      fullDescription: 'Прочные узы между членами семьи создают нерушимую основу для успеха.'
    }
  ]

  // Fetch API data with caching
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STATS}`, {
          method: 'GET',
          headers: getApiHeaders()
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()

        setApiData(data)
        setLastFetchTime(Date.now())
        setLoading(false)
      } catch (error) {
        console.error('Error fetching stats in About:', error)

        setApiData(mockStatsData)
        setLastFetchTime(Date.now())
        setLoading(false)
      }
    }

    const shouldFetchData = () => {
      if (!apiData) return true
      if (!lastFetchTime) return true
      const thirtyMinutes = 30 * 60 * 1000
      const timeSinceLastFetch = Date.now() - lastFetchTime
      return timeSinceLastFetch > thirtyMinutes
    }

    if (shouldFetchData()) {
      fetchStats()
    } else {
      setLoading(false)
    }
  }, [apiData, lastFetchTime])

  return (
    <section id="about" className="min-h-screen flex items-center bg-dark-800 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src="/content/about_bg.png" 
          alt="About background" 
          className="w-full h-full object-cover opacity-80 blur-sm"
        />
        <div className="absolute inset-0 bg-dark-800/80"></div>
        
        {/* Flying Icons */}
        <div className="hidden md:block">
          {flyingIcons.map((iconData) => (
            <motion.div
              key={iconData.id}
              className="absolute text-primary-400/10 blur-[0.7px]"
              style={{
                left: `${iconData.left}%`,
                top: `${iconData.top}%`,
                fontSize: `${iconData.fontSize}rem`,
              }}
              animate={{
                y: [0, -20, 0],
                x: [0, iconData.xOffset, 0],
                rotate: [0, 360],
              }}
              transition={{
                duration: iconData.duration,
                repeat: Infinity,
                ease: "linear",
                delay: iconData.delay,
              }}
            >
              {iconData.icon}
            </motion.div>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >

          
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-display font-bold text-white mb-3 sm:mb-4 md:mb-6 px-2 sm:px-4"
          >
            История становления{' '}
            <span className="gradient-text">Hugo Family</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-2 sm:px-4"
          >
            Основанная  цель создать самое комфортное и крупное комьюнити игроков, 
            где голос каждого будет важен и учтен.
          </motion.p>
        </motion.div>

        {/* Story Section */}
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 items-center mb-6 sm:mb-8 md:mb-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="space-y-3 sm:space-y-4 md:space-y-6 px-2 sm:px-4"
          >
            <h3 className="text-lg sm:text-xl md:text-2xl font-display font-bold text-white mb-2 sm:mb-3 md:mb-4">
              Наш путь
            </h3>
            <div className="space-y-2 sm:space-y-3 text-gray-300 text-xs sm:text-sm md:text-base">
              <p>
              Семья HUGO была основана в 2024 году Richard’ом Hugo. Вместе с Quincy и Logan они заложили
              фундамент будущего великого сообщества. Официальной датой рождения семьи стало
              13 апреля 2024 года — день, с которого началась стремительная история нашего роста.
              </p>
              <p>
              С самых первых дней HUGO уверенно развивалась, и вскоре заняла достойное место среди крупнейших
              государственных семей на Majestic RP. К нашей семье присоединились новые участники — не просто
              союзники, а настоящие друзья и единомышленники.
              </p>
              <p>
              Сегодня мы — одна из самых влиятельных и уважаемых семей на сервере.
              Мы не останавливаемся на достигнутом и продолжаем расти, укрепляя свои позиции.
              Присоединяйся к HUGO — стань частью легенды.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 mt-3 sm:mt-4">
            <motion.button
              onClick={onApplyClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-orange-600 to-orange-500 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-orange-500/25 relative overflow-hidden group text-xs sm:text-sm md:text-base"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <span className="relative z-10">Стань частью HUGO</span>
            </motion.button>
              
              <motion.button
                onClick={onViewFullHistory}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary text-xs sm:text-sm md:text-base px-3 sm:px-4 md:px-6 py-2 sm:py-3"
              >
                Смотреть полную историю
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="relative px-2 sm:px-4"
          >
            <div className="relative">
              {/* Glass effect background */}
              <div className="absolute inset-0 bg-white/3 backdrop-blur-sm rounded-3xl border border-white/5 shadow-lg"></div>
              
              {/* Content */}
              <div className="relative p-3 sm:p-4 md:p-6 lg:p-8">
                <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-6 lg:gap-8">
                                      <div className="text-center group transition-transform duration-300 hover:scale-110">
                      <div className="relative">
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 drop-shadow-2xl tracking-wide">
                          {loading ? '...' : (apiData?.users?.active_last_30_days || 95)}
                        </div> 
                        <div className="text-gray-300 text-xs sm:text-sm font-medium drop-shadow-lg uppercase tracking-wider">Активных участников</div>
                      </div>
                    </div>
                  
                                      <div className="text-center group transition-transform duration-300 hover:scale-110">
                      <div className="relative">
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 drop-shadow-2xl tracking-wide">3+</div>
                        <div className="text-gray-300 text-xs sm:text-sm font-medium drop-shadow-lg uppercase tracking-wider">Года вместе</div>
                      </div>
                    </div>
                  
                                      <div className="text-center group transition-transform duration-300 hover:scale-110">
                      <div className="relative">
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 drop-shadow-2xl tracking-wide">6</div>
                        <div className="text-gray-300 text-xs sm:text-sm font-medium drop-shadow-lg uppercase tracking-wider">Лидерских сроков</div>
                      </div>
                    </div>
                  
                                      <div className="text-center group transition-transform duration-300 hover:scale-110">
                      <div className="relative">
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 drop-shadow-2xl tracking-wide">100%</div>
                        <div className="text-gray-300 text-xs sm:text-sm font-medium drop-shadow-lg uppercase tracking-wider">Помощь софамов</div>
                      </div>
                    </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Values Section - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="text-center -mt-2 px-4"
        >
          <h3 className="text-sm sm:text-base font-display font-bold text-white mb-2">
            Основные принципы
          </h3>
          
          <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4 md:gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                className="relative flex items-center space-x-1 sm:space-x-2 text-gray-300 group hover:text-primary-400 transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-600/20 rounded-full flex items-center justify-center group-hover:bg-primary-600/30 transition-colors">
                  <value.icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary-400" />
                </div>
                <span className="text-xs sm:text-sm font-medium">{value.title}</span>
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none z-50">
                  <div className="bg-dark-800/90 border border-primary-500/30 rounded-lg p-3 shadow-xl w-48 sm:w-64">
                    <div className="text-white text-sm font-medium mb-2 text-center">{value.title}</div>
                    <div className="text-gray-300 text-xs leading-relaxed text-center">{value.fullDescription}</div>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-dark-800"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Scroll Arrow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ delay: 1, duration: 0.5 }}
        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 hidden sm:block"
      >
        <ScrollArrow targetId="statistics" />
      </motion.div>
      {/* Modal */}
      <AnimatePresence>
        {isStoryOpen && (
          <motion.div
            key="story-backdrop"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setIsStoryOpen(false)}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              className="relative z-10 w-full max-w-7xl bg-dark-800/95 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
              initial={{ y: 20, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 12, scale: 0.98, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
            >

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default About 